import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateAdminCompanyDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_COMPANIES } from "../domain";

import { ACTION_GET_ADMIN_COMPANIES } from "@api/hooks/companies/GetAdminCompanies";
import { companiesAdminApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_CREATE_ADMIN_COMPANY = "CREATE_ADMIN_COMPANY";

const allowedRoles = [UserRole.Admin];

interface CreateAdminCompanyResponse {
  id?: string;
}

const createAdminCompany = async (
  dto: CreateAdminCompanyDto,
): Promise<CreateAdminCompanyResponse> => {
  const response =
    await companiesAdminApi.companiesAdminControllerCreateAdminCompany(dto);

  return response.data;
};

export const useCreateAdminCompany = (
  user: UserProfileDto | null,
): {
  createAdminCompany: (dto: CreateAdminCompanyDto) => void;
  createAdminCompanyAsync: (
    dto: CreateAdminCompanyDto,
  ) => Promise<CreateAdminCompanyResponse>;
  createAdminCompanyEnabled: boolean;
  isSuccessCreateAdminCompany: boolean;
  isPendingCreateAdminCompany: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_CREATE_ADMIN_COMPANY],
    mutationFn: enabled ? createAdminCompany : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_ADMIN_COMPANIES],
      });
    },
  });

  return {
    createAdminCompany: mutate,
    createAdminCompanyAsync: mutateAsync,
    createAdminCompanyEnabled: enabled,
    isSuccessCreateAdminCompany: isSuccess,
    isPendingCreateAdminCompany: isPending,
  };
};
